// SaveStore (TECH_BASELINE §7.2): IndexedDB chính + 3 bản xoay vòng + mirror localStorage.
// Bản save hỏng KHÔNG BAO GIỜ bị ghi đè: nó được chép sang `corrupt:<ts>` rồi mới thử bản kế.
import type { KvBackend, MirrorBackend } from './backends';
import { decodeSave, encodeSave } from './codec';
import { FutureVersionError, migrate } from './migrations';
import { freshDoc, type SaveDoc } from './types';

export const MAIN_KEY = 'main';
export const BACKUP_KEYS = ['backup:0', 'backup:1', 'backup:2'] as const;
export const BACKUP_NEXT_KEY = 'meta:backupNext';
export const CORRUPT_PREFIX = 'corrupt:';
/** Doc lớn hơn thì bỏ mirror (TECH_BASELINE §7.2). */
export const MIRROR_MAX_BYTES = 2 * 1024 * 1024;

export type LoadSource = 'idb' | 'backup' | 'mirror' | 'fresh';

export interface LoadResult {
  doc: SaveDoc;
  source: LoadSource;
  /** Khóa `corrupt:*` vừa được tạo trong lần nạp này. */
  quarantined: string[];
  /** Doc từ phiên bản tương lai: chạy chỉ đọc, không ghi đè. */
  readOnly: boolean;
  /** Phiên bản gốc trước khi di trú (nếu có di trú). */
  migratedFrom?: number;
}

export interface SaveReport {
  idb: 'ok' | 'failed' | 'readOnly';
  mirror: 'ok' | 'failed' | 'tooLarge' | 'readOnly';
}

export interface SaveStore {
  load(): Promise<LoadResult>;
  save(doc: SaveDoc): Promise<SaveReport>;
  clear(): Promise<void>;
  /** Chuỗi xuất của bản đã lưu gần nhất. */
  exportString(): Promise<string>;
  /** Đã kiểm + di trú; CHƯA ghi (UI hỏi xác nhận rồi gọi save). */
  importString(s: string): Promise<SaveDoc>;
  /** Các khóa `corrupt:*` đang giữ (để xuất bản hỏng trong Thiết lập). */
  quarantinedKeys(): Promise<string[]>;
  /** Chuỗi thô của một bản đã cách ly. */
  readQuarantined(key: string): Promise<string | undefined>;
  readonly readOnly: boolean;
}

export interface SaveStoreDeps {
  kv: KvBackend;
  mirror: MirrorBackend;
  now: () => number;
  newSeed: () => number;
}

interface Candidate {
  source: Exclude<LoadSource, 'fresh'>;
  label: string;
  raw: string;
}

export function createSaveStore(deps: SaveStoreDeps): SaveStore {
  const { kv, mirror, now } = deps;
  let readOnly = false;
  let lastGood: string | null = null;

  async function quarantine(c: Candidate, out: string[]): Promise<void> {
    let key = `${CORRUPT_PREFIX}${now()}-${c.label}`;
    // Hai bản hỏng trong cùng mili giây vẫn phải có khóa riêng.
    for (let i = 1; (await kv.get(key)) !== undefined; i++) key = `${CORRUPT_PREFIX}${now()}-${c.label}-${i}`;
    await kv.set(key, c.raw);
    out.push(key);
  }

  function safeMirrorRead(): string | null {
    try {
      return mirror.read();
    } catch {
      return null;
    }
  }

  async function candidates(): Promise<Candidate[]> {
    const list: Candidate[] = [];
    const main = await kv.get(MAIN_KEY);
    if (main !== undefined) list.push({ source: 'idb', label: MAIN_KEY, raw: main });
    const backups: (Candidate & { at: number })[] = [];
    for (const k of BACKUP_KEYS) {
      const raw = await kv.get(k);
      if (raw === undefined) continue;
      let at = -Infinity;
      try {
        const p = JSON.parse(raw) as { savedAt?: unknown };
        if (typeof p.savedAt === 'number') at = p.savedAt;
      } catch {
        /* bản hỏng vẫn xếp hàng để bị cách ly */
      }
      backups.push({ source: 'backup', label: k.replace(':', ''), raw, at });
    }
    backups.sort((a, b) => b.at - a.at);
    list.push(...backups);
    const m = mirror.read();
    if (m !== null) list.push({ source: 'mirror', label: 'mirror', raw: m });
    return list;
  }

  return {
    get readOnly() {
      return readOnly;
    },

    async load() {
      const quarantined: string[] = [];
      let list: Candidate[];
      try {
        list = await candidates();
      } catch {
        // IndexedDB bị chặn (duyệt riêng tư, iframe hạn chế): vẫn thử mirror, không bao giờ chặn boot.
        const m = safeMirrorRead();
        list = m === null ? [] : [{ source: 'mirror', label: 'mirror', raw: m }];
      }
      for (const c of list) {
        try {
          const { doc, from } = migrate(JSON.parse(c.raw) as unknown);
          lastGood = JSON.stringify(doc);
          return {
            doc,
            source: c.source,
            quarantined,
            readOnly,
            ...(from !== doc.v ? { migratedFrom: from } : {}),
          };
        } catch (e) {
          if (e instanceof FutureVersionError) {
            // Mở bản cũ của app trên save mới: không đụng vào save, chạy chỉ đọc.
            readOnly = true;
            return { doc: freshDoc(now(), deps.newSeed()), source: c.source, quarantined, readOnly };
          }
          try {
            await quarantine(c, quarantined);
          } catch {
            /* không cất được bản hỏng thì vẫn bỏ qua nó, không chặn boot */
          }
        }
      }
      return { doc: freshDoc(now(), deps.newSeed()), source: 'fresh', quarantined, readOnly };
    },

    async save(doc) {
      if (readOnly) return { idb: 'readOnly', mirror: 'readOnly' };
      const s = JSON.stringify(doc);
      const report: SaveReport = { idb: 'ok', mirror: 'ok' };
      try {
        if (lastGood !== null && lastGood !== s) {
          const nextRaw = await kv.get(BACKUP_NEXT_KEY);
          const next = Number(nextRaw ?? 0) % BACKUP_KEYS.length;
          await kv.set(BACKUP_KEYS[next] ?? BACKUP_KEYS[0], lastGood);
          await kv.set(BACKUP_NEXT_KEY, String((next + 1) % BACKUP_KEYS.length));
        }
        await kv.set(MAIN_KEY, s);
      } catch {
        report.idb = 'failed';
      }
      // Độ dài chuỗi UTF-16 xấp xỉ số byte; đủ để chặn doc quá khổ.
      if (s.length > MIRROR_MAX_BYTES) {
        mirror.clear();
        report.mirror = 'tooLarge';
      } else if (!mirror.write(s)) {
        report.mirror = 'failed';
      }
      if (report.idb === 'ok' || report.mirror === 'ok') lastGood = s;
      return report;
    },

    async clear() {
      await kv.del(MAIN_KEY);
      for (const k of BACKUP_KEYS) await kv.del(k);
      await kv.del(BACKUP_NEXT_KEY);
      mirror.clear();
      lastGood = null;
    },

    async exportString() {
      const raw = lastGood ?? (await kv.get(MAIN_KEY)) ?? mirror.read();
      if (raw === null || raw === undefined) throw new Error('nothing saved yet');
      return encodeSave(migrate(JSON.parse(raw) as unknown).doc);
    },

    async importString(s) {
      return migrate(await decodeSave(s)).doc;
    },

    async quarantinedKeys() {
      return (await kv.keys()).filter((k) => k.startsWith(CORRUPT_PREFIX)).sort();
    },

    async readQuarantined(key) {
      return key.startsWith(CORRUPT_PREFIX) ? kv.get(key) : undefined;
    },
  };
}
